/**
 * Settings Helper Utility
 * 
 * Provides helper functions to parse and access student settings
 */

export const DEFAULT_LANGUAGE_SETTINGS = {
  audioEnabled: true,
  audioVolume: 80,
  readLanguageText: true,
  readEnglishText: false,
  showEnglishTranslation: true,
  textSize: 'medium' // small, medium, large
};

/**
 * Get the student's primary language from their subjects
 * @param {Object} student - Student object
 * @returns {string} Language code (e.g., 'kannada', 'hindi')
 */
function getStudentLanguageFromSubjects(student) {
  if (student?.subjects && student.subjects.length > 0) {
    return student.subjects[0].toLowerCase();
  }
  return 'kannada'; // default
}

/**
 * Parse student settings from JSON string
 * @param {Object} student - Student object with settings field
 * @param {string} language - Optional language to get settings for
 * @returns {Object} Parsed settings with defaults for the language
 */
export function getStudentSettings(student, language = null) {
  const targetLanguage = language || getStudentLanguageFromSubjects(student);
  
  if (!student?.settings) {
    return DEFAULT_LANGUAGE_SETTINGS;
  }

  try {
    const parsed = JSON.parse(student.settings);
    const languageSettings = parsed[targetLanguage] || {};
    
    // Merge with defaults to ensure all fields exist
    return {
      ...DEFAULT_LANGUAGE_SETTINGS,
      ...languageSettings
    };
  } catch (error) {
    return DEFAULT_LANGUAGE_SETTINGS;
  }
}

/**
 * Get audio settings for Learning Mode
 * @param {Object} student - Student object
 * @returns {Object} Learning mode audio settings
 */
export function getLearningModeAudioSettings(student) {
  const settings = getStudentSettings(student);
  return {
    languageAudio: settings.audioEnabled,
    languageVolume: settings.audioVolume,
    englishAudio: settings.audioEnabled,
    englishVolume: settings.audioVolume
  };
}

/**
 * Get audio settings for Activities
 * @param {Object} student - Student object
 * @returns {Object} Activities audio settings
 */
export function getActivitiesAudioSettings(student) {
  const settings = getStudentSettings(student);
  return {
    languageAudio: settings.audioEnabled,
    languageVolume: settings.audioVolume,
    englishAudio: settings.audioEnabled,
    englishVolume: settings.audioVolume,
    successAudio: settings.audioEnabled,
    successVolume: settings.audioVolume,
    errorAudio: settings.audioEnabled,
    errorVolume: settings.audioVolume
  };
}

/**
 * Check if language audio should play
 * @param {Object} student - Student object
 * @param {string} context - 'learningMode' or 'activities'
 * @returns {boolean}
 */
export function shouldPlayLanguageAudio(student, context = 'activities') {
  const settings = getStudentSettings(student);
  return settings.audioEnabled;
}

/**
 * Check if English audio should play
 * @param {Object} student - Student object
 * @param {string} context - 'learningMode' or 'activities'
 * @returns {boolean}
 */
export function shouldPlayEnglishAudio(student, context = 'activities') {
  const settings = getStudentSettings(student);
  return settings.audioEnabled;
}

/**
 * Get volume level for language audio
 * @param {Object} student - Student object
 * @param {string} context - 'learningMode' or 'activities'
 * @returns {number} Volume (0-100)
 */
export function getLanguageVolume(student, context = 'activities') {
  const settings = getStudentSettings(student);
  return settings.audioVolume || 80;
}

/**
 * Get volume level for English audio
 * @param {Object} student - Student object
 * @param {string} context - 'learningMode' or 'activities'
 * @returns {number} Volume (0-100)
 */
export function getEnglishVolume(student, context = 'activities') {
  const settings = getStudentSettings(student);
  return settings.audioVolume || 80;
}

/**
 * Get text size setting
 * @param {Object} student - Student object
 * @returns {string} 'small', 'medium', or 'large'
 */
export function getTextSize(student) {
  const settings = getStudentSettings(student);
  return settings.textSize || 'medium';
}

/**
 * Get text size class for Tailwind CSS
 * @param {Object} student - Student object
 * @returns {string} Tailwind text size class
 */
export function getTextSizeClass(student) {
  const size = getTextSize(student);
  const sizeMap = {
    small: 'text-base',
    medium: 'text-lg',
    large: 'text-xl'
  };
  return sizeMap[size] || 'text-lg';
}

/**
 * Check if language text should be shown
 * @param {Object} student - Student object
 * @returns {boolean}
 */
export function shouldShowLanguageText(student) {
  const settings = getStudentSettings(student);
  return settings.readLanguageText !== false;
}

/**
 * Check if English text should be shown
 * @param {Object} student - Student object
 * @returns {boolean}
 */
export function shouldShowEnglishText(student) {
  const settings = getStudentSettings(student);
  return settings.readEnglishText || false;
}

/**
 * Check if English translation should be shown
 * @param {Object} student - Student object
 * @returns {boolean}
 */
export function shouldShowEnglishTranslation(student) {
  const settings = getStudentSettings(student);
  return settings.showEnglishTranslation !== false;
}

/**
 * Get the student's primary language
 * @param {Object} student - Student object
 * @returns {string} Language code (e.g., 'kannada', 'hindi', 'tamil')
 */
export function getStudentLanguage(student) {
  return getStudentLanguageFromSubjects(student);
}

/**
 * Get the display name for the student's language
 * @param {Object} student - Student object
 * @returns {string} Language display name (e.g., 'Kannada', 'Hindi')
 */
export function getStudentLanguageDisplayName(student) {
  const language = getStudentLanguage(student);
  const languageNames = {
    kannada: 'Kannada',
    hindi: 'Hindi',
    tamil: 'Tamil',
    telugu: 'Telugu',
    malayalam: 'Malayalam',
    english: 'English'
  };
  return languageNames[language] || 'Language';
}

/**
 * Check if success audio should play
 * @param {Object} student - Student object
 * @returns {boolean}
 */
export function shouldPlaySuccessAudio(student) {
  const settings = getStudentSettings(student);
  return settings.audioEnabled;
}

/**
 * Check if error audio should play
 * @param {Object} student - Student object
 * @returns {boolean}
 */
export function shouldPlayErrorAudio(student) {
  const settings = getStudentSettings(student);
  return settings.audioEnabled;
}

/**
 * Get volume level for success audio
 * @param {Object} student - Student object
 * @returns {number} Volume (0-100)
 */
export function getSuccessVolume(student) {
  const settings = getStudentSettings(student);
  return settings.audioVolume || 80;
}

/**
 * Get volume level for error audio
 * @param {Object} student - Student object
 * @returns {number} Volume (0-100)
 */
export function getErrorVolume(student) {
  const settings = getStudentSettings(student);
  return settings.audioVolume || 80;
}
